import { type GridApiPro, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid-pro";
import { type Dispatch, type MutableRefObject, type SetStateAction } from "react";

import ThreeDotMenu from "../../../../global/components/ThreeDotMenu";
import { CoAppLightTooltip } from "../../../../global/styled/global.styled";
import IdpGroupAutocompleteCell from "../IdpGroupAutocompleteCell";
import { ScimWizardRoleNameCell } from "../styled/scim-wizard.styled";

interface ExternalGroupOption {
	externalGroupId: string;
	externalGroupName: string;
	[key: string]: unknown;
}

export interface Step5RoleRow {
	id: string | number;
	name: string;
	isSuperAdmin?: boolean;
	idpGroups?: ExternalGroupOption[];
	[key: string]: unknown;
}

type MappedRoles = Record<string, ExternalGroupOption[]>;

export const getStep5RoleColumns = (
	identityProviderName: string,
	idpGroups: ExternalGroupOption[],
	mappedRoles: MappedRoles,
	setMappedRoles: Dispatch<SetStateAction<MappedRoles>>,
	apiRef: MutableRefObject<GridApiPro | null>,
	validateSuperAdminRoleIsMapped: (mappings: unknown, type: string) => void,
	roleMappingStatus: Record<string, unknown>,
	initializeExternalGroups: () => Promise<void> | void,
	setIdpGroups: Dispatch<SetStateAction<ExternalGroupOption[]>>,
	toggleEditRoleDialog: (role: Step5RoleRow) => void,
	toggleDeleteRoleDialog: (role: Step5RoleRow) => void
): GridColDef<Step5RoleRow>[] => [
	{
		field: "name",
		headerName: "CoApp roles",
		sortable: true,
		flex: 1,
		renderCell: (params: GridRenderCellParams<Step5RoleRow, string>) => (
			<ScimWizardRoleNameCell
				variant="body2"
				noWrap={true}
				data-superadmin={params.row.isProtected ? "true" : "false"}
			>
				{params.value}
			</ScimWizardRoleNameCell>
		)
	},
	{
		field: "idpGroups",
		headerName: `${identityProviderName} groups`,
		sortable: false,
		flex: 1,
		renderCell: (params: GridRenderCellParams<Step5RoleRow>) => (
			<IdpGroupAutocompleteCell
				options={idpGroups}
				mapped={mappedRoles}
				setMapped={setMappedRoles}
				resourceName={params.row.name}
				apiRef={apiRef}
				isSuperAdmin={params.row.isProtected}
				validateSuperAdminRoleIsMapped={validateSuperAdminRoleIsMapped}
				roleMappingStatus={roleMappingStatus}
				initializeGroupOptions={initializeExternalGroups}
				setExternalGroups={setIdpGroups}
			/>
		)
	},
	{
		field: "actions",
		type: "actions",
		sortable: false,
		flex: .1,
		renderCell: (params: GridRenderCellParams<Step5RoleRow>) => (
			params.row.isProtected ?
				<CoAppLightTooltip title="Super Admin role cannot be edited" placement="right">
					<div>
						<ThreeDotMenu options={[]} isCoAppSuperAdmin={true} />
					</div>
				</CoAppLightTooltip>
				:
				<div>
					<ThreeDotMenu
						options={[
							{
								name: "Edit",
								optionClickHandler: () => {
									toggleEditRoleDialog(params.row);
								},
							},
							{
								name: "Delete",
								optionClickHandler: () => {
									toggleDeleteRoleDialog(params.row);
								},
							},
						]}
					/>
				</div>
		)
	}
];
